import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useLock } from '../../context/LockContext';
import { useTheme } from '../../context/ThemeContext';
import { useWorkMode } from '../../context/WorkModeContext';
import { useSidebar } from '../../context/SidebarContext';
import UserMenu from '../UserMenu';
import Icon from '../Icon';
import LanguageSelector from '../LanguageSelector';
import HelpCenterMenu from '../help/HelpCenterMenu';
import { storage } from '../../utils/storage';
import { listMyRestaurants } from '../../services/api';
import './Header.css';

function Header() {
  const { t } = useTranslation();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isWorkModeOpen, setIsWorkModeOpen] = useState(false);
  const { user } = useAuth();
  const { hasPin, lock } = useLock();
  const { preference, cyclePreference } = useTheme();

  const themeIcon = { light: 'light_mode', dark: 'dark_mode', auto: 'brightness_auto' }[preference];
  const themeLabel = t(`theme.${preference}`);
  const { workMode, setWorkMode, availableModes } = useWorkMode();
  const { isMobileMenuOpen, toggleMobileMenu } = useSidebar();

  const toggleUserMenu = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
    setIsWorkModeOpen(false);
  };

  const closeUserMenu = () => {
    setIsUserMenuOpen(false);
  };

  const toggleWorkModeMenu = () => {
    setIsWorkModeOpen(!isWorkModeOpen);
    setIsUserMenuOpen(false);
  };

  const handleSelectWorkMode = (mode) => {
    setWorkMode(mode);
    setIsWorkModeOpen(false);
  };

  const handleLock = () => {
    lock();
  };

  const currentModeConfig = availableModes[workMode];

  // Restaurante activo: visible en el header para que se entienda sobre qué
  // restaurante se trabaja y dónde cambiarlo (menú de usuario). QA pasada 2:
  // el nombre solo se guardaba al cambiar de restaurante, así que si falta se
  // resuelve una vez desde la lista de restaurantes de la organización.
  const [activeRestaurantName, setActiveRestaurantName] = useState(
    storage.get('selectedRestaurantName') || ''
  );
  useEffect(() => {
    if (activeRestaurantName) return undefined;
    const restaurantId = storage.get('selectedRestaurantId');
    const organizationId = storage.get('selectedOrganizationId') || storage.get('organizationId');
    if (!restaurantId || !organizationId) return undefined;
    let cancelled = false;
    listMyRestaurants(organizationId)
      .then((data) => {
        const match = (data?.restaurants || []).find((r) => r.restaurantId === restaurantId);
        if (!match || cancelled) return;
        storage.set('selectedRestaurantName', match.name);
        setActiveRestaurantName(match.name);
      })
      .catch((error) => {
        console.warn('[Header] could not resolve active restaurant name:', error?.message);
      });
    return () => {
      cancelled = true;
    };
  }, [activeRestaurantName]);

  // NER-4030: nombre a mostrar en el badge. Preferir name/email (Google y
  // email/password) antes que username (que en Google es el sub/UUID).
  const displayName = user?.name || user?.email || user?.attributes || user?.username;

  return (
    <header className="header-new">
      <div className="header-left">
        <button
          type="button"
          className="mobile-menu-btn"
          onClick={toggleMobileMenu}
          aria-label={
            isMobileMenuOpen
              ? t('common.closeMenu', 'Cerrar menú')
              : t('common.openMenu', 'Abrir menú')
          }
        >
          <Icon name={isMobileMenuOpen ? 'close' : 'menu'} />
        </button>
        <div className="header-logo">
          <Icon name="restaurant_menu" className="logo-icon" />
          <span className="logo-text">Resto</span>
        </div>
      </div>

      <div className="header-right">
        {/* Work Mode Selector */}
        <div className="work-mode-container">
          <button
            type="button"
            className="work-mode-btn"
            onClick={toggleWorkModeMenu}
            title={t('workMode.selectMode', 'Seleccionar modo')}
            aria-label={t('workMode.selectMode', 'Seleccionar modo')}
          >
            <Icon name={currentModeConfig?.icon || 'work'} size="small" />
            <span className="work-mode-label">
              {currentModeConfig?.labelKey ? t(currentModeConfig.labelKey) : workMode}
            </span>
            <Icon
              name="arrow_drop_down"
              size="small"
              className={`dropdown-arrow ${isWorkModeOpen ? 'open' : ''}`}
            />
          </button>

          {isWorkModeOpen && (
            <div className="work-mode-dropdown">
              {Object.entries(availableModes).map(([key, config]) => (
                <button
                  type="button"
                  key={key}
                  className={`work-mode-option ${workMode === key ? 'active' : ''}`}
                  onClick={() => handleSelectWorkMode(key)}
                >
                  <Icon name={config.icon} size="small" />
                  <span>{t(config.labelKey)}</span>
                  {workMode === key && <Icon name="check" size="small" className="check-icon" />}
                </button>
              ))}
            </div>
          )}
        </div>

        <LanguageSelector />

        <HelpCenterMenu />

        <button
          type="button"
          className="header-icon-btn"
          onClick={cyclePreference}
          aria-label={`${t('theme.title')}: ${themeLabel}`}
          title={`${t('theme.title')}: ${themeLabel}`}
        >
          <Icon name={themeIcon} size="small" />
        </button>

        {hasPin && (
          <button
            type="button"
            className="header-icon-btn"
            onClick={handleLock}
            aria-label="Lock screen"
            title={t('lock.lock')}
          >
            <Icon name="lock" size="small" />
          </button>
        )}

        {activeRestaurantName && (
          <button
            type="button"
            className="header-restaurant-chip"
            onClick={toggleUserMenu}
            title={t('restaurants.switchRestaurant')}
            aria-label={`${t('restaurants.restaurant')}: ${activeRestaurantName}`}
          >
            <Icon name="store" size="small" />
            <span className="header-restaurant-chip__name">{activeRestaurantName}</span>
          </button>
        )}

        <div className="user-menu-container">
          <button
            type="button"
            className="user-menu-btn"
            onClick={toggleUserMenu}
            aria-label="User menu"
          >
            <div className="header-user-avatar">
              {displayName?.charAt(0).toUpperCase() || <Icon name="person" size="small" />}
            </div>
            <span className="user-name">{displayName}</span>
            <Icon
              name="arrow_drop_down"
              size="small"
              className={`dropdown-arrow ${isUserMenuOpen ? 'open' : ''}`}
            />
          </button>

          {isUserMenuOpen && <UserMenu onClose={closeUserMenu} />}
        </div>
      </div>

      {/* Overlay to close menus */}
      {(isUserMenuOpen || isWorkModeOpen) && (
        <div
          className="menu-overlay"
          onClick={() => {
            closeUserMenu();
            setIsWorkModeOpen(false);
          }}
        ></div>
      )}
    </header>
  );
}

export default Header;
